import React from "react";
import { Link } from "react-router-dom";
import { PaDrawer, PaStatusBadge } from "components/platform-admin";
import StripeDashboardLink from "../Billing/StripeDashboardLink";
import {
  TENANT_ISSUE_LABELS,
  TENANT_ISSUE_VARIANTS,
  analyzeTenantAccount,
  getTenantStripeLinks,
  getTenantSupportPlaybooks,
} from "./tenantSupport.utils";
import { getTenantOperationalPlaybooks } from "./tenantOperationalPlaybooks.utils";
import { getTenantStatusBadge } from "./tenantsList.utils";
import { formatSubscriptionStatusLabel } from "./tenantOverview.utils";

const COMMERCIAL_ISSUES = new Set(["active_subscription", "no_subscription", "cancelled"]);

const SupportPlaybookSection = ({
  title,
  description,
  playbooks,
  onRunAction,
  tenant,
}) => {
  if (!playbooks.length) {
    return null;
  }

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description ? (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      ) : null}

      <ul className="mt-4 space-y-4">
        {playbooks.map((playbook) => (
          <li
            key={playbook.id}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{playbook.title}</p>
                <p className="mt-1 text-sm text-gray-600">{playbook.description}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                {playbook.primaryAction ? (
                  <button
                    type="button"
                    onClick={() =>
                      onRunAction(playbook.primaryAction.id, tenant, playbook)
                    }
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {playbook.primaryAction.label}
                  </button>
                ) : null}
                {playbook.secondaryAction ? (
                  <button
                    type="button"
                    onClick={() =>
                      onRunAction(playbook.secondaryAction.id, tenant, playbook)
                    }
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {playbook.secondaryAction.label}
                  </button>
                ) : null}
              </div>
            </div>

            {playbook.selfServiceSteps?.length ? (
              <div className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/60 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-900">
                  Customer self-service
                </p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-600">
                  {playbook.selfServiceSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            ) : null}

            {playbook.steps?.length ? (
              <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-gray-600">
                {playbook.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

const TenantSupportDrawer = ({
  open,
  onClose,
  tenant,
  permissions = {},
  onRunAction,
}) => {
  if (!tenant) {
    return null;
  }

  const account = analyzeTenantAccount(tenant);
  const billingPlaybooks = getTenantSupportPlaybooks(tenant, permissions);
  const operationalPlaybooks = getTenantOperationalPlaybooks(tenant, permissions);
  const stripeLinks = getTenantStripeLinks(tenant, permissions);
  const statusBadge = getTenantStatusBadge(tenant.status);
  const subscription = tenant.subscription || {};
  const visibleIssues = permissions.billingView
    ? account.issues
    : account.issues.filter((issue) => !COMMERCIAL_ISSUES.has(issue));
  const subscriptionSummary = permissions.billingView
    ? `${subscription.plan_name || "No plan"} · ${formatSubscriptionStatusLabel(subscription) || "No subscription status"}`
    : formatSubscriptionStatusLabel(subscription) || "No subscription status";

  return (
    <PaDrawer
      open={open}
      onClose={onClose}
      title="Tenant support"
      description={`Guided fixes for ${tenant.name}`}
      widthClassName="max-w-xl"
    >
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">{tenant.name}</p>
              <p className="text-sm text-gray-600">{tenant.owner?.email || tenant.email || "—"}</p>
              <p className="mt-1 text-xs text-gray-500">{subscriptionSummary}</p>
            </div>
            {statusBadge ? (
              <PaStatusBadge label={statusBadge.label} variant={statusBadge.variant} />
            ) : null}
          </div>

          {visibleIssues.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {visibleIssues.map((issue) => (
                <PaStatusBadge
                  key={issue}
                  label={TENANT_ISSUE_LABELS[issue] || issue}
                  variant={TENANT_ISSUE_VARIANTS[issue] || "neutral"}
                />
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-gray-600">No access issues flagged.</p>
          )}
        </section>

        {stripeLinks.length > 0 ? (
          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900">Stripe</h3>
            <p className="mt-1 text-sm text-gray-500">
              Open this tenant&apos;s billing records in the Stripe Dashboard.
            </p>
            <ul className="mt-3 space-y-2">
              {stripeLinks.map((link) => (
                <li
                  key={link.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {link.label}
                    </p>
                    {link.resourceId ? (
                      <p className="font-mono text-xs text-gray-700">{link.resourceId}</p>
                    ) : null}
                  </div>
                  {link.href ? (
                    <StripeDashboardLink
                      href={link.href}
                      className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                      title={`Open ${link.label} in Stripe`}
                    >
                      Open in Stripe
                    </StripeDashboardLink>
                  ) : (
                    <span className="text-xs text-gray-400">No dashboard link</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <SupportPlaybookSection
          title="Operational guides"
          description="Common product how-to requests. Open in tenant lands on the relevant screen."
          playbooks={operationalPlaybooks}
          onRunAction={onRunAction}
          tenant={tenant}
        />

        <SupportPlaybookSection
          title="Account & billing"
          description="Tenant access, billing, and offboarding actions."
          playbooks={billingPlaybooks}
          onRunAction={onRunAction}
          tenant={tenant}
        />

        <section>
          <h3 className="text-sm font-semibold text-gray-900">Quick links</h3>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            {permissions.billingView ? (
              <>
                <Link
                  to={`/tenants/${tenant.id}?tab=billing`}
                  className="font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Billing tab
                </Link>
                <Link to="/stripe" className="font-medium text-indigo-600 hover:text-indigo-700">
                  Stripe management
                </Link>
                {stripeLinks
                  .filter((link) => link.href)
                  .map((link) => (
                    <StripeDashboardLink
                      key={link.id}
                      href={link.href}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                      title={`Open ${link.label} in Stripe`}
                    >
                      {link.label}
                    </StripeDashboardLink>
                  ))}
              </>
            ) : null}
            <Link to="/audit-log" className="font-medium text-indigo-600 hover:text-indigo-700">
              Audit log
            </Link>
          </div>
        </section>
      </div>
    </PaDrawer>
  );
};

export default TenantSupportDrawer;
